/**
 * Status-bar scale maths — spec §F5.10 ("`zoom n · ~1:scale`").
 *
 * The Web-Mercator denominator at zoom 0 for a 256 px tile and a 96 dpi (0.28 mm) pixel is
 * 591 657 550.5; every zoom level halves it. Mercator stretches east-west away from the
 * equator, so the ground scale at the map centre is the equator value times cos(latitude).
 * The result is rounded to thousands — a status bar states an order of magnitude, not a
 * survey-grade denominator.
 */
import { fmtNum } from '../../lib/format';

/** 1:scale denominator at zoom 0 on the equator (256 px tiles, 96 dpi). */
export const SCALE_DENOM_Z0 = 591657550.5;

export function mapScale(zoom: number, lat = 0): number {
  const clamped = Math.max(-89.9, Math.min(89.9, lat));
  const raw = (SCALE_DENOM_Z0 / Math.pow(2, zoom)) * Math.cos((clamped * Math.PI) / 180);
  return Math.round(raw / 1000) * 1000;
}

/** `zoom 5 · ~1:18,489,000` */
export function formatScale(zoom: number, lat = 0): string {
  return `zoom ${fmtNum(zoom, 0)} · ~1:${fmtNum(mapScale(zoom, lat), 0)}`;
}

/** `lat 12.3456, lon -1.5432` — §F5.10 "lat, lon mousemove", 4 decimals. */
export function formatCoords(lat: number | null, lon: number | null): string {
  if (lat === null || lon === null || !Number.isFinite(lat) || !Number.isFinite(lon)) {
    return 'lat —, lon —';
  }
  return `lat ${lat.toFixed(4)}, lon ${lon.toFixed(4)}`;
}
